<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { IonContent, IonButton, IonIcon, IonSpinner, IonChip, IonLabel } from '@ionic/vue';
import { chevronBack, calendar, time, star } from 'ionicons/icons';
import { useI18n } from 'vue-i18n';
import { anykrowdApi } from '@/api/anykrowdApi';
import useToast from '@/shared/composables/useToast';
import { useAuthStore } from '@/stores/auth/authStore';
import useQueue from '@/modules/queue/composables/useQueue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { presentToast } = useToast();
const authStore = useAuthStore();
const { bookEventTicketSlot } = useQueue();

const activityId = computed(() => route.params.activityId as string);
const isFromUnbooked = computed(() => route.query.from_unbooked === 'true');
const unbookedEventTicketIds = ref<number[]>([]);
const unbookedQueueTypes = ref<string[]>([]);
const isBooking = ref(false);

const isLoading = ref(false);
const schedules = ref<any[]>([]);
const selectedDate = ref<string | null>(null);
const selectedTimeSlot = ref<string | null>(null);
const bookingType = ref<'standalone' | 'event_linked'>('standalone');
const queueType = ref<'chronological' | 'time_slot'>('chronological');
const activity = ref<any>(null);
const userCurrentBookings = ref<number>(0);

// Group schedules by date
const groupedSchedules = computed(() => {
	const groups: Record<string, any[]> = {};

	schedules.value.forEach(schedule => {
		// Make sure we have a valid date
		if (schedule.date) {
			if (!groups[schedule.date]) {
				groups[schedule.date] = [];
			}
			groups[schedule.date].push(schedule);
		}
	});

	return groups;
});

// Get unique dates
const availableDates = computed(() => {
	return Object.keys(groupedSchedules.value).sort();
});

// Get time slots for selected date
const availableTimeSlots = computed(() => {
	if (!selectedDate.value) return [];

	return groupedSchedules.value[selectedDate.value] || [];
});

// Helper functions to check booking availability
const isPastDate = (dateStr: string) => {
	// Parse the date string properly (add time component to avoid timezone issues)
	const date = new Date(dateStr + 'T00:00:00');
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	date.setHours(0, 0, 0, 0);
	return date < today;
};

const isPastTimeSlot = (dateStr: string, startTimeStr: string, endTimeStr?: string) => {
	if (!startTimeStr) return false;

	// Use end time if provided (from schedule.end_time), otherwise use start time
	const timeToCheck = endTimeStr || startTimeStr;
	const [hours, minutes] = timeToCheck.split(':').map(Number);
	const slotDate = new Date(dateStr);
	slotDate.setHours(hours, minutes, 0, 0);

	// Time slot is only past if the end time has passed
	return slotDate < new Date();
};

const hasExceededBookingLimit = () => {
	// If we don't have activity data yet, don't restrict
	if (!activity.value) return false;

	// Get max bookings, default to 0 (no limit) if not set
	const maxBookings = activity.value.max_bookings_per_user || 0;

	// If 0, there's no limit
	if (maxBookings === 0) return false;

	// Check if user has reached or exceeded the limit
	return userCurrentBookings.value >= maxBookings;
};

const isSlotSoldOut = (schedule: any) => {
	// Check if slot is inactive
	if (schedule.is_active === 0 || schedule.is_active === false) return true;
	// Check if capacity is explicitly set to 0 or if available is false
	if (schedule.available === false) return true;
	if (schedule.capacity !== undefined && schedule.capacity <= 0) return true;

	// Check availability from the new availability structure
	if (schedule.availability) {
		// If booking from unbooked tickets, check availability for the specific queue type
		if (isFromUnbooked.value && unbookedQueueTypes.value.length > 0) {
			const currentQueueType = unbookedQueueTypes.value[0] || 'normal';
			const queueTypeKey = currentQueueType === 'fastlane' ? 'fastlane' : 'normal';
			const typeAvail = schedule.availability[queueTypeKey]?.available ?? 0;
			// Sold out if the specific queue type has no availability
			return typeAvail <= 0;
		}

		// Otherwise, sold out if both normal and fastlane have no available slots
		const normalAvail = schedule.availability.normal?.available ?? 0;
		const fastlaneAvail = schedule.availability.fastlane?.available ?? 0;
		if (normalAvail <= 0 && fastlaneAvail <= 0) {
			return true;
		}
	}

	return false;
};

// Check if an entire date is sold out (all time slots are sold out)
const isDateSoldOut = (date: string) => {
	const schedulesForDate = groupedSchedules.value[date] || [];

	// If no schedules for this date, consider it sold out
	if (schedulesForDate.length === 0) return true;

	// Check if ALL time slots for this date are sold out
	// Date is sold out only if every single slot has no availability
	const allSlotsSoldOut = schedulesForDate.every((schedule: any) => {
		if (schedule.is_active === 0 || schedule.is_active === false) return true;
		if (schedule.available === false) return true;
		if (schedule.capacity !== undefined && schedule.capacity <= 0) return true;

		// Check availability from the new availability structure
		if (schedule.availability) {
			// If booking from unbooked tickets, check availability for the specific queue type
			if (isFromUnbooked.value && unbookedQueueTypes.value.length > 0) {
				const currentQueueType = unbookedQueueTypes.value[0] || 'normal';
				const queueTypeKey = currentQueueType === 'fastlane' ? 'fastlane' : 'normal';
				const typeAvail = schedule.availability[queueTypeKey]?.available ?? 0;
				// Slot is sold out if the specific queue type has no availability
				return typeAvail <= 0;
			}

			// Otherwise, slot is sold out if both normal and fastlane have no availability
			const normalAvail = schedule.availability.normal?.available ?? 0;
			const fastlaneAvail = schedule.availability.fastlane?.available ?? 0;
			return normalAvail <= 0 && fastlaneAvail <= 0;
		}

		return false;
	});

	return allSlotsSoldOut;
};

// Get total available capacity across all time slots for a date
const getDateTotalCapacity = (date: string) => {
	const schedulesForDate = groupedSchedules.value[date] || [];

	// Sum up available capacity from all time slots (normal + fastlane)
	let totalAvailable = 0;
	schedulesForDate.forEach((schedule: any) => {
		if (schedule.availability) {
			const normalAvail = schedule.availability.normal?.available ?? 0;
			const fastlaneAvail = schedule.availability.fastlane?.available ?? 0;
			totalAvailable += normalAvail + fastlaneAvail;
		} else if (schedule.capacity !== undefined && schedule.capacity > 0) {
			// Fallback to schedule capacity if availability structure not present
			totalAvailable += schedule.capacity;
		}
	});

	return totalAvailable;
};

const isDateBookable = (date: string, scheduleData: any) => {
	// Check if date is in the past
	if (isPastDate(date)) return false;

	// Check if entire date is sold out (all time slots)
	if (isDateSoldOut(date)) return false;

	// Check if user has exceeded booking limit
	if (hasExceededBookingLimit()) return false;

	// Check if within advance booking hours
	if (activity.value && activity.value.advance_booking_hours > 0) {
		const bookingDate = new Date(date);
		const minBookingDate = new Date();
		minBookingDate.setHours(minBookingDate.getHours() + activity.value.advance_booking_hours);
		if (bookingDate < minBookingDate) return false;
	}

	return scheduleData.available !== false;
};

const isTimeSlotBookable = (date: string, slot: any) => {
	// Check if time slot is in the past (check end time, not start time)
	if (isPastTimeSlot(date, slot.start_time, slot.end_time)) return false;

	// Check if sold out
	if (isSlotSoldOut(slot)) return false;

	// Check if user has exceeded booking limit
	if (hasExceededBookingLimit()) return false;

	// Check if within advance booking hours
	if (activity.value && activity.value.advance_booking_hours > 0 && slot.start_time) {
		const [hours, minutes] = slot.start_time.split(':').map(Number);
		const slotDateTime = new Date(date);
		slotDateTime.setHours(hours, minutes, 0, 0);
		const minBookingDate = new Date();
		minBookingDate.setHours(minBookingDate.getHours() + activity.value.advance_booking_hours);
		if (slotDateTime < minBookingDate) return false;
	}

	return slot.available !== false;
};

// Check if continue button should be enabled
const canContinue = computed(() => {
	// Always allow continuing if date is selected (for both standalone and event-linked)
	if (!selectedDate.value) return false;

	// For time_slot mode, require time slot selection
	if (queueType.value === 'time_slot' && !selectedTimeSlot.value) {
		return false;
	}

	return true;
});

// Queue type display for unbooked tickets
const queueTypeDisplay = computed(() => {
	if (!isFromUnbooked.value || unbookedQueueTypes.value.length === 0) return null;

	// Show queue type for the CURRENT ticket being booked (first in the list)
	const currentQueueType = unbookedQueueTypes.value[0] || 'normal';

	if (currentQueueType === 'fastlane') {
		return { type: 'fastlane', label: t('queue.fastlane'), showIcon: false };
	}

	return { type: 'normal', label: t('queue.normal_queue'), showIcon: false };
});

const remainingTicketsCount = computed(() => {
	if (!isFromUnbooked.value) return 0;
	return unbookedEventTicketIds.value.length;
});

const loadSchedules = async () => {
	isLoading.value = true;

	try {
		// Fetch schedules and activity details
		const [schedulesResponse, activityResponse] = await Promise.all([
			anykrowdApi.get(`/queue/activities/${activityId.value}/schedules`, true),
			anykrowdApi.get(`/queue/activities/${activityId.value}`, true)
		]);

		bookingType.value = schedulesResponse.data.booking_type;
		queueType.value = schedulesResponse.data.queue_type;

		// Filter schedules to only include those with valid dates and that are active
		const fetchedSchedules = schedulesResponse.data.schedules || [];
		schedules.value = fetchedSchedules.filter((schedule: any) => {
			// Only include schedules that have a date and are active
			return schedule.date && (schedule.is_active === 1 || schedule.is_active === true || schedule.is_active === undefined);
		});

		activity.value = activityResponse.data;

		// Check if user booking count is provided in the activity or schedules response
		// Since the user-bookings endpoint doesn't exist, we'll look for this info in the main responses
		if (authStore.isAuthenticated()) {
			// Check if the activity response includes user booking info
			userCurrentBookings.value = activityResponse.data.user_bookings_count ||
									   schedulesResponse.data.user_bookings_count ||
									   0;
		}

		// Auto-select if only one date available
		if (schedulesResponse.data.schedules.length === 1) {
			selectedDate.value = schedulesResponse.data.schedules[0].date;
		}

		// For event-linked, auto-select the event date
		if (schedulesResponse.data.booking_type === 'event_linked' && schedulesResponse.data.schedules.length > 0) {
			selectedDate.value = schedulesResponse.data.schedules[0].date;
		}
	} catch (error: any) {
		console.error('Error loading schedules:', error);
		await presentToast('top', t('queue.error_loading_schedules'), 3000, 'danger');
		router.back();
	} finally {
		isLoading.value = false;
	}
};

const selectDate = (date: string) => {
	// Check if date is bookable
	const scheduleForDate = groupedSchedules.value[date][0];
	if (!isDateBookable(date, scheduleForDate)) {
		// Show appropriate warning message
		if (isPastDate(date)) {
			presentToast('top', t('queue.error_past_date'), 3000, 'warning');
		} else if (isDateSoldOut(date)) {
			presentToast('top', t('queue.error_sold_out'), 3000, 'warning');
		} else if (hasExceededBookingLimit()) {
			presentToast('top', t('queue.error_booking_limit_exceeded'), 3000, 'warning');
		} else {
			presentToast('top', t('queue.error_not_available'), 3000, 'warning');
		}
		return;
	}

	selectedDate.value = date;
	selectedTimeSlot.value = null; // Reset time slot when date changes

	// If chronological queue, we can proceed with just the date
	if (queueType.value === 'chronological') {
		const hasNoTimeSlots = !groupedSchedules.value[date][0].start_time;
		if (hasNoTimeSlots) {
			// Auto-select the schedule without time slot
			selectedTimeSlot.value = null;
		}
	}
};

const selectTimeSlot = (schedule: { start_time: string; end_time?: string; [key: string]: any }) => {
	// Check if time slot is bookable
	if (!selectedDate.value || !isTimeSlotBookable(selectedDate.value, schedule)) {
		// Show appropriate warning message
		if (selectedDate.value && isPastTimeSlot(selectedDate.value, schedule.start_time, schedule.end_time)) {
			presentToast('top', t('queue.error_past_time_slot'), 3000, 'warning');
		} else if (selectedDate.value && isSlotSoldOut(schedule)) {
			presentToast('top', t('queue.error_sold_out'), 3000, 'warning');
		} else if (hasExceededBookingLimit()) {
			presentToast('top', t('queue.error_booking_limit_exceeded'), 3000, 'warning');
		} else {
			presentToast('top', t('queue.error_not_available'), 3000, 'warning');
		}
		return;
	}

	selectedTimeSlot.value = `${formatTime(schedule.start_time)}-${formatTime(schedule.end_time || '')}`;
};

const formatTime = (timeStr: string) => {
	// Remove seconds from time string (HH:MM:SS -> HH:MM)
	if (!timeStr) return '';
	const parts = timeStr.split(':');
	if (parts.length >= 2) {
		return `${parts[0]}:${parts[1]}`;
	}
	return timeStr;
};

const handleBooking = async () => {
	if (!canContinue.value) return;

	if (isFromUnbooked.value && unbookedEventTicketIds.value.length > 0 && selectedDate.value) {
		// Direct booking flow for unbooked tickets - book ONE ticket at a time
		isBooking.value = true;

		try {
			// Book only the first ticket
			const eventTicketId = unbookedEventTicketIds.value[0];
			const queueType = unbookedQueueTypes.value[0] || 'normal';

			// Extract start time from selectedTimeSlot format "HH:MM-HH:MM" to "HH:MM"
			let timeSlotForApi: string | undefined = undefined;
			if (selectedTimeSlot.value) {
				// If format is "HH:MM-HH:MM", extract just the start time "HH:MM"
				if (selectedTimeSlot.value.includes('-')) {
					timeSlotForApi = selectedTimeSlot.value.split('-')[0];
				} else {
					timeSlotForApi = selectedTimeSlot.value;
				}
			}

			await bookEventTicketSlot(eventTicketId, {
				target_date: selectedDate.value,
				target_time_slot: timeSlotForApi,
				queue_type: queueType,
			});

			// Remove the booked ticket from the list
			const remainingTicketIds = unbookedEventTicketIds.value.slice(1);
			const remainingQueueTypes = unbookedQueueTypes.value.slice(1);

			// Clear stored data if no more tickets
			if (remainingTicketIds.length === 0) {
				sessionStorage.removeItem('unbooked_event_ticket_ids');
				sessionStorage.removeItem('unbooked_queue_types');
				sessionStorage.removeItem('booking_event_ticket_ids');

				await presentToast('top', t('queue.all_tickets_booked_successfully'), 3000, 'success');
				router.push('/my-queues');
			} else {
				// Update session storage with remaining tickets
				sessionStorage.setItem('unbooked_event_ticket_ids', JSON.stringify(remainingTicketIds));
				sessionStorage.setItem('unbooked_queue_types', JSON.stringify(remainingQueueTypes));

				// Update local state
				unbookedEventTicketIds.value = remainingTicketIds;
				unbookedQueueTypes.value = remainingQueueTypes;

				// Reset date/time selection for next ticket
				selectedDate.value = null;
				selectedTimeSlot.value = null;

				// Show success message and indicate remaining tickets
				const remainingCount = remainingTicketIds.length;
				if (remainingCount > 0) {
					await presentToast('top', t('queue.ticket_booked_continue_with_remaining', { count: remainingCount }), 3000, 'success');
				}
			}
		} catch (error: any) {
			console.error('Error during booking:', error);
			// Show error message - don't show success message on error
			const errorMessage = error?.response?.data?.message || error?.message || t('queue.booking_failed');
			await presentToast('top', errorMessage, 5000, 'danger');
			// Don't update state or show success message on error
		} finally {
			isBooking.value = false;
		}

		return;
	}

	// Regular flow: navigate to ticket selection
	if (isFromUnbooked.value && unbookedEventTicketIds.value.length > 0) {
		sessionStorage.setItem('booking_event_ticket_ids', JSON.stringify(unbookedEventTicketIds.value));
	}

	router.push({
		name: 'queue-tickets-buy',
		params: {
			activityId: activityId.value,
		},
		query: {
			date: selectedDate.value,
			timeSlot: selectedTimeSlot.value || undefined,
			from_unbooked: isFromUnbooked.value ? 'true' : undefined,
		}
	});
};

onMounted(() => {
	// Load unbooked event ticket IDs if coming from unbooked flow
	if (isFromUnbooked.value) {
		const storedIds = sessionStorage.getItem('unbooked_event_ticket_ids');
		if (storedIds) {
			try {
				unbookedEventTicketIds.value = JSON.parse(storedIds);
			} catch (e) {
				console.error('Error parsing unbooked event ticket IDs:', e);
			}
		}

		// Load queue types
		const storedQueueTypes = sessionStorage.getItem('unbooked_queue_types');
		if (storedQueueTypes) {
			try {
				unbookedQueueTypes.value = JSON.parse(storedQueueTypes);
			} catch (e) {
				console.error('Error parsing unbooked queue types:', e);
			}
		}
	}
	loadSchedules();
});
</script>

<template>
	<ion-content class="queue-date-selection">
		<div class="container">
			<!-- Header with back button and title -->
			<div class="header-section">
				<div class="flex items-center px-6 pt-16 pb-6">
					<ion-button fill="clear" class="back-button" @click="router.back()">
						<ion-icon :icon="chevronBack" class="back-icon" />
					</ion-button>
					<h1 class="header-title">{{ t('queue.select_date_time') }}</h1>
				</div>
				<p class="page-subtitle">{{ t('queue.choose_preferred_slot') }}</p>
			</div>

			<!-- Queue Type Indicator (for unbooked tickets) -->
			<div v-if="isFromUnbooked && queueTypeDisplay" class="queue-type-indicator">
				<div v-if="remainingTicketsCount > 1" class="remaining-tickets-badge">
					{{ t('queue.booking_ticket_of_total', { current: 1, total: remainingTicketsCount }) }}
				</div>
				<div class="queue-type-badge" :class="`queue-type-${queueTypeDisplay.type}`">
					<ion-icon v-if="queueTypeDisplay.showIcon" :icon="star" class="queue-type-icon"></ion-icon>
					<span class="queue-type-label">{{ queueTypeDisplay.label }}</span>
				</div>
			</div>

			<!-- Loading State -->
			<div v-if="isLoading" class="loading-container">
				<ion-spinner name="dots" />
			</div>

			<!-- Content -->
			<div v-else-if="schedules.length === 0" class="empty-state-container">
				<div class="empty-state">
					<div class="empty-icon">??</div>
					<h2 class="empty-title">{{ t('queue.no_schedules_available') }}</h2>
					<p class="empty-description">{{ t('queue.no_schedules_description') }}</p>
					<ion-button @click="router.back()" class="mt-4">
						{{ t('general.go_back') }}
					</ion-button>
				</div>
			</div>

			<!-- Content -->
			<div v-else class="content-wrapper">

				<!-- Date Selection -->
				<div class="date-selection-section">
					<div class="section-header">
						<ion-icon :icon="calendar" class="section-icon" />
						<h2 class="section-title">{{ t('queue.select_date') }}</h2>
					</div>

					<div class="date-grid">
						<div
							v-for="date in availableDates"
							:key="date"
							class="date-card"
							:class="{
								'selected': selectedDate === date,
								'disabled': !isDateBookable(date, groupedSchedules[date][0]),
								'past': isPastDate(date),
								'sold-out': isDateSoldOut(date),
								'limit-exceeded': hasExceededBookingLimit() && !isPastDate(date) && !isDateSoldOut(date)
							}"
							@click="selectDate(date)">
							<div class="date-day">{{ new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' }) }}</div>
							<div class="date-number">{{ new Date(date + 'T00:00:00').getDate() }}</div>
							<div class="date-month">{{ new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short' }) }}</div>
							<div v-if="isDateSoldOut(date)" class="status-badge sold-out-badge">
								{{ t('queue.sold_out') }}
							</div>
							<div v-else-if="isPastDate(date)" class="status-badge past-badge">
								{{ t('queue.past') }}
							</div>
							<div v-else-if="hasExceededBookingLimit()" class="status-badge limit-badge">
								{{ t('queue.limit_reached') }}
							</div>
							<div v-else-if="!isDateSoldOut(date) && getDateTotalCapacity(date) <= 5 && getDateTotalCapacity(date) > 0" class="capacity-warning">
								{{ getDateTotalCapacity(date) }} {{ t('queue.spots_left') }}
							</div>
						</div>
					</div>
				</div>

				<!-- Time Slot Selection (when time slots exist) -->
				<div v-if="selectedDate && availableTimeSlots.length > 0 && availableTimeSlots[0].start_time" class="time-selection-section">
					<div class="section-header">
						<ion-icon :icon="time" class="section-icon" />
						<h2 class="section-title">{{ t('queue.select_time_slot') }}</h2>
					</div>

					<div class="time-slots-grid">
						<ion-chip
							v-for="slot in availableTimeSlots"
							:key="`${slot.start_time}-${slot.end_time}`"
							:class="{
								'selected': selectedTimeSlot === `${formatTime(slot.start_time)}-${formatTime(slot.end_time)}`,
								'disabled': !isTimeSlotBookable(selectedDate, slot),
								'past': isPastTimeSlot(selectedDate, slot.start_time, slot.end_time),
								'sold-out': isSlotSoldOut(slot),
								'limit-exceeded': hasExceededBookingLimit() && !isPastTimeSlot(selectedDate, slot.start_time, slot.end_time) && !isSlotSoldOut(slot)
							}"
							@click="selectTimeSlot(slot)">
							<ion-label>
								{{ formatTime(slot.start_time) }} - {{ formatTime(slot.end_time) }}
								<span v-if="isSlotSoldOut(slot)" class="status-text sold-out-text">
									{{ t('queue.sold_out') }}
								</span>
								<span v-else-if="isPastTimeSlot(selectedDate, slot.start_time, slot.end_time)" class="status-text past-text">
									{{ t('queue.past') }}
								</span>
								<span v-else-if="hasExceededBookingLimit()" class="status-text limit-text">
									{{ t('queue.limit_reached') }}
								</span>
								<span v-else-if="slot.capacity <= 5 && slot.capacity > 0" class="capacity-badge">
									({{ slot.capacity }})
								</span>
							</ion-label>
						</ion-chip>
				</div>
			</div>

			<!-- Bottom Button -->
			<div class="bottom-bar">
				<div class="bottom-bar-backdrop"></div>
				<div class="bottom-bar-content">
				<ion-button
					expand="block"
					:disabled="!canContinue || isBooking"
					@click="handleBooking"
					class="continue-button">
					<ion-spinner v-if="isBooking" name="dots" class="mr-2"></ion-spinner>
					{{ isFromUnbooked ? t('queue.book_now') : t('queue.continue_to_tickets') }}
				</ion-button>
				</div>
			</div>
		</div>
	</div>
</ion-content>
</template>

<style lang="scss" scoped>
.queue-date-selection {
	--background: #17161e;
}

.container {
	max-width: 600px;
	margin: 0 auto;
	min-height: 100vh;
	display: flex;
	flex-direction: column;
	background: #17161e;
}

// Header
.header-section {
	background: #17161e;

	.back-button {
		--color: #fff;
		--padding-start: 0;
		--padding-end: 0;
		width: 40px;
		height: 40px;
		margin-right: 16px;
	}

	.back-icon {
		font-size: 24px;
		color: #fff;
	}

	.header-title {
		font-family: 'Source Sans Pro', -apple-system, BlinkMacSystemFont, sans-serif;
		font-size: 20px;
		font-weight: 600;
		color: #fff;
		margin: 0;
	}

	.page-subtitle {
		font-family: 'Source Sans Pro', -apple-system, BlinkMacSystemFont, sans-serif;
		font-size: 16px;
		color: rgba(255, 255, 255, 0.5);
		margin: 0;
		padding: 0 24px 24px 24px;
	}
}

// Loading
.loading-container {
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;

	ion-spinner {
		--color: #fff;
		width: 48px;
		height: 48px;
	}
}

// Empty state
.empty-state-container {
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;

	.empty-state {
		text-align: center;
		max-width: 400px;

		.empty-icon {
			font-size: 64px;
			margin-bottom: 24px;
		}

		.empty-title {
			font-size: 24px;
			font-weight: 700;
			color: #fff;
			margin: 0 0 12px 0;
		}

		.empty-description {
			font-size: 16px;
			color: rgba(255, 255, 255, 0.6);
			margin: 0 0 24px 0;
		}

		ion-button {
			--background: var(--ion-color-primary);
			--background-activated: var(--ion-color-primary-shade);
			--border-radius: 12px;
			font-weight: 600;

			&:hover {
				opacity: 0.8;
				--background: var(--ion-color-primary) !important;
			}
		}
	}
}

// Content
.content-wrapper {
	flex: 1;
	padding: 0 34px 100px;
	overflow-y: auto;
}

// Date Selection
.date-selection-section {
	margin-bottom: 32px;
}

.section-header {
	display: flex;
	align-items: center;
	margin-bottom: 16px;

	.section-icon {
		font-size: 24px;
		color: #fff;
		margin-right: 12px;
	}

	.section-title {
		font-size: 20px;
		font-weight: 600;
		color: #fff;
		margin: 0;
	}
}

.date-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
	gap: 12px;
}

.date-card {
	background: #2a2935;
	border: 2px solid #3d3b4a;
	border-radius: 12px;
	padding: 16px 8px;
	text-align: center;
	cursor: pointer;
	transition: all 0.3s ease;

	.date-day,
	.date-number,
	.date-month {
		color: rgba(255, 255, 255, 0.9);
	}

	&:hover:not(.disabled) {
		background: #343240;
		border-color: #4a4856;
	}

	&.selected {
		border-color: var(--ion-color-primary);

		.date-day,
		.date-number,
		.date-month {
			color: #fff;
		}
	}

	&.disabled {
		opacity: 0.4;
		cursor: not-allowed;
		pointer-events: none;

		&.past {
			background: #2a2935;
			border-color: #444;
		}

		&.sold-out {
			background: #3a2935;
			border-color: #544;
		}

		&.limit-exceeded {
			background: #352a35;
			border-color: #544;
		}
	}

	.date-day {
		font-size: 12px;
		text-transform: uppercase;
		margin-bottom: 4px;
	}

	.date-number {
		font-size: 24px;
		font-weight: 700;
		margin-bottom: 4px;
	}

	.date-month {
		font-size: 14px;
	}

	.capacity-warning {
		font-size: 10px;
		color: #ff9800;
		margin-top: 8px;
		font-weight: 600;
	}

	.status-badge {
		font-size: 10px;
		margin-top: 8px;
		font-weight: 600;
		padding: 2px 4px;
		border-radius: 4px;

		&.sold-out-badge {
			color: #ff5252;
			background: rgba(255, 82, 82, 0.1);
		}

		&.past-badge {
			color: #888;
			background: rgba(136, 136, 136, 0.1);
		}

		&.limit-badge {
			color: #ffc107;
			background: rgba(255, 193, 7, 0.1);
		}
	}
}

// Time Slot Selection
.time-selection-section {
	margin-bottom: 32px;
}

.time-slots-grid {
	display: flex;
	flex-wrap: wrap;
	gap: 12px;

	ion-chip {
		--background: rgba(255, 255, 255, 0.05);
		--color: #fff;
		height: 48px;
		font-size: 16px;
		padding: 0 20px;
		cursor: pointer;
		transition: all 0.3s ease;

		&:hover:not(.disabled) {
			--background: rgba(255, 255, 255, 0.1);
		}

		&.selected {
			border: 2px solid var(--ion-color-primary);
		}

		&.disabled {
			opacity: 0.4;
			cursor: not-allowed;
			pointer-events: none;

			&.past {
				--background: rgba(100, 100, 100, 0.05);
			}

			&.sold-out {
				--background: rgba(200, 50, 50, 0.05);
			}

			&.limit-exceeded {
				--background: rgba(200, 150, 50, 0.05);
			}
		}

		.capacity-badge {
			margin-left: 8px;
			color: #ff9800;
			font-weight: 600;
		}

		.status-text {
			margin-left: 8px;
			font-size: 12px;
			font-weight: 600;

			&.sold-out-text {
				color: #ff5252;
			}

			&.past-text {
				color: #888;
			}

			&.limit-text {
				color: #ffc107;
			}
		}
	}
}

// Event-linked Notice
.event-linked-notice {
	background: rgba(255, 255, 255, 0.05);
	border-radius: 16px;
	padding: 24px;
	text-align: center;

	.notice-icon {
		font-size: 48px;
		margin-bottom: 16px;
	}

	h3 {
		font-size: 20px;
		font-weight: 600;
		color: #fff;
		margin: 0 0 8px 0;
	}

	p {
		font-size: 16px;
		color: rgba(255, 255, 255, 0.6);
		margin: 0 0 16px 0;
	}

	.event-date {
		font-size: 24px;
		font-weight: 700;
		color: var(--ion-color-primary);
	}
}

// Queue Type Indicator
.queue-type-indicator {
	padding: 0 24px 16px 24px;
	display: flex;
	flex-direction: column;
	gap: 12px;

	.remaining-tickets-badge {
		font-family: 'Source Sans Pro', sans-serif;
		font-size: 14px;
		font-weight: 600;
		color: rgba(255, 255, 255, 0.8);
		text-align: center;
		padding: 8px 16px;
		background: rgba(255, 255, 255, 0.1);
		border-radius: 12px;
		border: 1px solid rgba(255, 255, 255, 0.15);
	}

	.queue-type-badge {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		padding: 8px 16px;
		border-radius: 20px;
		font-size: 14px;
		font-weight: 600;

		&.queue-type-fastlane {
			background: rgba(255, 193, 7, 0.2);
			border: 1px solid rgba(255, 193, 7, 0.5);
			color: #ffc107;
		}

		&.queue-type-normal {
			background: rgba(255, 255, 255, 0.1);
			border: 1px solid rgba(255, 255, 255, 0.2);
			color: rgba(255, 255, 255, 0.8);
		}

		.queue-type-icon {
			font-size: 16px;
			width: 16px;
			height: 16px;
		}

		.queue-type-label {
			font-family: 'Source Sans Pro', sans-serif;
		}
	}
}

// Bottom Bar
.bottom-bar {
	position: fixed;
	bottom: 0;
	left: 0;
	right: 0;
	z-index: 100;
	height: 96px;

	.bottom-bar-backdrop {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		height: 96px;
		background: rgba(23, 22, 30, 0.8);
		backdrop-filter: blur(20px);
		-webkit-backdrop-filter: blur(20px);
	}

	.bottom-bar-content {
		position: relative;
		padding: 14px 30px 30px 30px;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	ion-button.continue-button {
		--background: #ffffff;
		--color: #18171e;
		--border-radius: 52px;
		--padding-top: 14px;
		--padding-bottom: 14px;
		--padding-start: 30px;
		--padding-end: 30px;
		font-family: 'Source Sans Pro', -apple-system, BlinkMacSystemFont, sans-serif;
		font-size: 18px;
		font-weight: 600;
		line-height: 0;
		text-align: center;
		min-height: 52px;
		width: 100%;
		max-width: 325px;

		&:hover {
			--background: #ffffff !important;
			--color: #18171e;
		}

		&:active,
		&.ion-activated {
			--background: #f5f5f5;
			--color: #18171e;
		}

		&:disabled {
			--background: #666666;
			--color: #999999;
		}

		.spinner-in-button {
			margin-right: 8px;
		}
	}
}
</style>
